import { useCallback, useState } from 'react'
import clsx from 'clsx'

import {
    Avatar,
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    makeStyles,
    Theme,
    useMediaQuery
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined'

import { ReactComponent as CollapsedNav } from 'assets/icons/collapsedNav.svg'
import { TextOverflow } from '@guidde-co/shared.text-overflow.text-overflow'
import { useBoolean } from '@guidde-co/shared.hooks.use-boolean'

import { StartUsingMessage } from 'UI/Routes/main/common'
import { SpacedGroup, VisibilitySensor } from 'UI/Components'

import { PlaybookType, QuickGuiddeType, Shape, TextShapeType } from 'app/types'

const pageHeader = 64
const drawerWidthMax = 326
const drawerWidthMin = 64
const navHeader = 44

type StyleProps = {
    bigScreen: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
    pointer: {
        cursor: 'pointer'
    },
    listItem: {
        borderRadius: '4px'
    },
    navContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        overflow: 'hidden',
        backgroundColor: 'white',
        border: 'solid rgba(9, 12, 16, 0.08) 1px',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    navHeader: {
        whiteSpace: 'nowrap',
        fontWeight: 'bold',
        fontSize: '14px',
        height: navHeader,
        borderTop: 'solid rgba(9, 12, 16, 0.08) 1px',
        borderBottom: 'solid rgba(9, 12, 16, 0.08) 1px'
    },
    playIcon: {
        width: '18px',
        height: '18px',
        color: '#1C1B1F',
        '&:hover': {
            color: 'white'
        }
    },
    stepText: {
        fontWeight: 300,
        fontSize: '12px',
        color: '#000000'
    },
    selectedItem: {
        backgroundColor: '#EDEEEF'
    },
    playAvatar: {
        width: '24px',
        height: '24px',
        marginRight: '10px',
        backgroundColor: '#EDEEEF'
    },
    selectedPlayAvatar: {
        backgroundColor: '#ffffff'
    },
    listWithScroll: {
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '20px'
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D8D8D8',
            borderRadius: '20px',
            border: '6px solid transparent',
            backgroundClip: 'content-box'
        }
    },
    // Height of navigation without startUsing dialog = height of navigation header (44) + custom number (10)
    heightWithoutStartUsing: {
        height: `calc(100% - ${navHeader + 10}px)`
    },
    // Height of navigation with startUsing dialog = height of navigation header (44) + custom number (380/240) according
    // to screen height const bigScreen = useMediaQuery('(min-height:720px)')
    heightWithStartUsing: {
        transition: 'all 0.2s linear',
        height: ({ bigScreen }) =>
            `calc(100% - ${pageHeader + navHeader + (bigScreen ? 380 : 240)}px)`
    }
}))
type Props = {
    playbook: PlaybookType
    onDrawerSizeChange?: (newSize: number) => void
    showStartUsing?: boolean
}

export const QGNavigation = ({ playbook, showStartUsing = false }: Props) => {
    const isMinimalMenu = useBoolean()

    // special treatment for small height screens, all the rest works as expected
    const bigScreen = useMediaQuery('(min-height:720px)')

    const classes = useStyles({ bigScreen })

    const [selectedOption, setSelectedOption] = useState<string | null>(null)

    const getStepTitle = useCallback((step: QuickGuiddeType) => {
        // if title exists return it
        if (step.title) return step.title

        //Steps has no title, search for the first text layer and return its text if exists
        const firstText = step.layers.find(layer => layer.type === Shape.Text) as
            | TextShapeType
            | undefined

        return firstText?.title || ''
    }, [])

    return (
        <Box
            className={classes.navContainer}
            width={isMinimalMenu.isTrue ? drawerWidthMin : drawerWidthMax}
        >
            {/* Closed menu */}
            <SpacedGroup flexDirection="column" display={isMinimalMenu.isTrue ? 'flex' : 'none'}>
                <SpacedGroup justifyContent="center" pt={1}>
                    <IconButton onClick={isMinimalMenu.setFalse} style={{ width: 36, height: 36 }}>
                        <CollapsedNav />
                    </IconButton>
                </SpacedGroup>
                <Box px={1} style={{ marginTop: '5px' }}>
                    <Divider />
                </Box>
            </SpacedGroup>

            {/* Opened menu */}
            <Box display={isMinimalMenu.isTrue ? 'none' : 'block'} height="100%">
                <SpacedGroup
                    px={2}
                    justifyContent="space-between"
                    alignItems="center"
                    className={classes.navHeader}
                >
                    Table of contents
                    <IconButton size="small" onClick={isMinimalMenu.setTrue}>
                        <CloseIcon />
                    </IconButton>
                </SpacedGroup>

                <Box
                    px={2}
                    className={clsx(classes.listItem, classes.listWithScroll, {
                        [classes.heightWithoutStartUsing]: !showStartUsing,
                        [classes.heightWithStartUsing]: showStartUsing
                    })}
                >
                    <List className={classes.pointer}>
                        <Box
                            className={clsx(classes.listItem, {
                                [classes.selectedItem]: selectedOption === 'video'
                            })}
                        >
                            <ListItem
                                key="video"
                                button={true}
                                onClick={() => {
                                    window.scrollTo({
                                        top: 0,
                                        behavior: 'smooth'
                                    })

                                    setSelectedOption('video')
                                }}
                            >
                                <SpacedGroup alignContent="center">
                                    <Avatar
                                        className={clsx(classes.playAvatar, {
                                            [classes.selectedPlayAvatar]: selectedOption === 'video'
                                        })}
                                    >
                                        <PlayArrowOutlinedIcon
                                            className={classes.playIcon}
                                            data-cy="scroll-to-video"
                                        />
                                    </Avatar>
                                    <Box>Video</Box>
                                </SpacedGroup>
                            </ListItem>
                        </Box>
                        {playbook.steps
                            .filter(step => step.kind === 'step')
                            .map((step, idx) => (
                                <VisibilitySensor
                                    key={step.id}
                                    placeholder={<span>&nbsp;</span>}
                                    threshold={0.01}
                                    delay={100}
                                >
                                    <Box
                                        key={step.id}
                                        className={clsx(classes.listItem, {
                                            [classes.selectedItem]: selectedOption === step.id
                                        })}
                                    >
                                        <ListItem
                                            button={true}
                                            className={classes.pointer}
                                            onClick={() => {
                                                const element = document.getElementById(step.id)
                                                if (element) {
                                                    element.scrollIntoView({
                                                        behavior: 'smooth'
                                                    })
                                                }
                                                setSelectedOption(step.id)
                                            }}
                                        >
                                            <SpacedGroup>
                                                <Avatar
                                                    className={clsx(classes.playAvatar, {
                                                        [classes.selectedPlayAvatar]:
                                                            selectedOption === step.id
                                                    })}
                                                >
                                                    <Box className={classes.stepText}>
                                                        {(idx + 1).toString().padStart(2, '0')}
                                                    </Box>
                                                </Avatar>
                                                <TextOverflow lineClamp={1}>
                                                    {getStepTitle(step)}
                                                </TextOverflow>
                                            </SpacedGroup>
                                        </ListItem>
                                    </Box>
                                </VisibilitySensor>
                            ))}
                    </List>
                </Box>
                {showStartUsing && (
                    <>
                        <Box px={1} my={2}>
                            <Divider />
                        </Box>
                        <Box>
                            <StartUsingMessage playbook={playbook} />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}
